import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import ReCAPTCHA from 'react-google-recaptcha';
import { Alert, Box, Divider, Paper, Skeleton, Typography } from '@mui/material';
import { apiPost } from './utils/apiAuth';
import { GoogleSignInButton } from './components/ui';

const LOGO_SRC = `${process.env.PUBLIC_URL}/Exskilence logo.svg`;

function LoginSkeleton({ showRecaptcha }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        width: '100%',
      }}
      aria-busy="true"
      aria-label="Signing in"
    >
      {showRecaptcha && (
        <Skeleton
          variant="rounded"
          width={304}
          height={78}
          sx={{ borderRadius: 1 }}
        />
      )}
      <Skeleton variant="rounded" width="100%" height={44} />
      <Skeleton variant="text" width="55%" height={20} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Signing in…
      </Typography>
    </Box>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);

  const isRecaptchaEnabled = process.env.REACT_APP_RECAPTCHA === 'true';
  const canSignIn = !isRecaptchaEnabled || recaptchaVerified;

  const clearError = () => setError('');

  const resetRecaptcha = useCallback(() => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setRecaptchaVerified(false);
  }, []);

  const onRecaptchaChange = (token) => {
    setRecaptchaVerified(Boolean(token));
    if (token) {
      clearError();
    }
  };

  const onRecaptchaExpired = () => {
    setRecaptchaVerified(false);
  };

  const onRecaptchaError = () => {
    setRecaptchaVerified(false);
    setError('reCAPTCHA verification failed. Please try again.');
  };

  const handleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      clearError();
      setUser(codeResponse);
    },
    onError: () => {
      setError('Google sign-in was cancelled or failed. Please try again.');
    },
  });

  useEffect(() => {
    if (!user) return;

    const fetchUserProfile = async () => {
      setLoading(true);
      clearError();

      try {
        const res = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${user.access_token}`
        );
        const { name, email } = res.data;
        sessionStorage.setItem('Name', name);
        sessionStorage.setItem('Email', email);

        const json = {
          email: CryptoJS.enc.Utf8.parse(email).toString(),
          access_token: CryptoJS.enc.Utf8.parse(user.access_token).toString(),
        };

        try {
          const response = await apiPost('/api/suite/login/', json);
          const data = response.data;

          const roles = Array.isArray(data.Type)
            ? data.Type.map((r) => String(r).trim()).filter(Boolean)
            : String(data.Type || '')
                .split(',')
                .map((r) => r.trim())
                .filter(Boolean);

          // CC authors content; AD has the same create/save access (approve stays AD-only in UI/API)
          const canAccessContentCreation =
            data.exists && (roles.includes('CC') || roles.includes('AD'));

          if (canAccessContentCreation) {
            sessionStorage.setItem('Type', roles.join(','));
            sessionStorage.setItem('access_token', data.access_token);
            if (data.Id) {
              sessionStorage.setItem('userId', data.Id);
            }
            if (data.access) {
              const accessList = Array.isArray(data.access)
                ? data.access
                : String(data.access)
                    .split(',')
                    .map((a) => a.trim())
                    .filter(Boolean);
              sessionStorage.setItem('access', accessList.join(','));
            }
            navigate('/dashboard');
            return;
          }

          setError('Your account is not authorized for Content Creation. Contact your administrator.');
          resetRecaptcha();
        } catch {
          setError('Unable to verify your account. Please try again.');
          resetRecaptcha();
        }
      } catch {
        setError('Failed to load your Google profile. Please try again.');
        resetRecaptcha();
      } finally {
        setLoading(false);
        setUser(null);
      }
    };

    fetchUserProfile();
  }, [user, navigate, resetRecaptcha]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(145deg, var(--color-primary-50) 0%, var(--color-neutral-100) 45%, var(--color-secondary-light) 100%)`,
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--color-border-default)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src={LOGO_SRC}
            alt="Exskilence"
            sx={{
              height: 40,
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              mb: 2,
            }}
          />
          <Typography
            variant="h5"
            component="h1"
            align="center"
            fontWeight={700}
            color="text.primary"
            sx={{ lineHeight: 1.3 }}
          >
            Content Creation Suite
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 0.75, maxWidth: 300 }}
          >
            Sign in with your Google account to create and manage LMS content.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error ? (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        ) : null}

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
          {loading ? (
            <LoginSkeleton showRecaptcha={isRecaptchaEnabled} />
          ) : (
            <>
              {isRecaptchaEnabled && process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    onErrored={onRecaptchaError}
                  />
                </Box>
              ) : null}

              <GoogleSignInButton
                onClick={() => {
                  clearError();
                  handleLogin();
                }}
                disabled={!canSignIn}
              />

              {isRecaptchaEnabled && !canSignIn ? (
                <Typography variant="caption" color="text.secondary" align="center">
                  Complete the reCAPTCHA check to continue.
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
